// firebaseService.js
import { doc, updateDoc, arrayUnion, getDoc, runTransaction } from 'firebase/firestore'
import { db } from '../firebase'
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage'
import { v4 as uuidv4 } from 'uuid'
import { applyMarkerWrite } from './applyMarkerWrite'

const storage = getStorage()

export const deleteAttachmentFromStorage = async (fileUrl, thumbnailUrl) => {
  try {
    if (fileUrl) {
      const fileRef = ref(storage, fileUrl)
      await deleteObject(fileRef)
    }
    if (thumbnailUrl) {
      const thumbRef = ref(storage, thumbnailUrl)
      await deleteObject(thumbRef)
    }
    return true
  } catch (error) {
    if (error.code === 'storage/object-not-found') {
      console.warn('File already deleted or not found in storage')
      return true
    }
    console.error('Error deleting attachment from storage:', error)
    throw error
  }
}

export const createIncidentMapMarker = async (incidentId, markerData) => {
  try {
    const incidentRef = doc(db, 'incidents', incidentId)

    await updateDoc(incidentRef, {
      mapMarkers: arrayUnion({
        // id: markerData.pin_id,
        ...markerData
      })
    })

    return markerData
  } catch (error) {
    console.error('Error adding map marker:', error)
    throw error
  }
}

// Pins live in the `mapMarkers` array on the incident document and several
// people edit that one array at the same time. arrayRemove matches a map by
// deep equality, so removing a pin whose stored copy has drifted removes
// nothing and the paired arrayUnion leaves a duplicate. A pin's identity is
// its `id`, so read, replace by id, and write, inside a transaction. Untouched
// entries are written back untouched.
const writeMapMarkers = (incidentId, markerId, update) =>
  runTransaction(db, async (txn) => {
    const incidentRef = doc(db, 'incidents', incidentId)
    const snap = await txn.get(incidentRef)
    if (!snap.exists()) throw new Error('Incident not found')

    txn.update(incidentRef, {
      mapMarkers: applyMarkerWrite(snap.data().mapMarkers, markerId, update)
    })
  })

export const updateIncidentMapMarker = (incidentId, markerId, updatedData) =>
  writeMapMarkers(incidentId, markerId, updatedData).then(() => true)

export const deleteIncidentMapMarker = (incidentId, markerId) =>
  writeMapMarkers(incidentId, markerId, null)

export const generateImageThumbnail = (file, maxWidth = 300, maxHeight = 300) => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = (event) => {
      const img = new Image()
      img.src = event.target.result
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width
            width = maxWidth
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height
            height = maxHeight
          }
        }

        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            resolve(blob)
          },
          'image/jpeg',
          0.8
        )
      }
      img.onerror = () => resolve(null)
    }
    reader.onerror = () => resolve(null)
  })
}

export const generateVideoThumbnail = (file) => {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    const url = URL.createObjectURL(file)
    video.src = url
    video.preload = 'metadata'
    video.muted = true
    video.playsInline = true

    video.onloadedmetadata = () => {
      video.currentTime = 1 // Capture frame at 1 second
    }

    video.onseeked = () => {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(url)
      canvas.toBlob(
        (blob) => {
          resolve(blob)
        },
        'image/jpeg',
        0.8
      )
    }

    video.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
  })
}

export const uploadAttachments = async ({
  fileList,
  incident,
  pinId,
  userId
}) => {
  const folderPath = `${process.env.REACT_APP_ENV === 'production' ? 'prod/' : 'stage/'
    }incidents/${incident.id}`

  const uploads = fileList.map(async (file) => {
    const fileObj = file.originFileObj
    const fileId = uuidv4()
    const timestamp = Date.now()

    const storageRef = ref(storage, `${folderPath}/${timestamp}_${fileObj.name}`)

    await uploadBytes(storageRef, fileObj)
    const downloadURL = await getDownloadURL(storageRef)

    // Generate and upload thumbnail if image or video
    let thumbnailURL = null
    try {
      let thumbnailBlob = null
      if (file.type?.startsWith('image/')) {
        thumbnailBlob = await generateImageThumbnail(fileObj)
      } else if (file.type?.startsWith('video/')) {
        thumbnailBlob = await generateVideoThumbnail(fileObj)
      }

      if (thumbnailBlob) {
        const thumbRef = ref(
          storage,
          `${folderPath}/thumb_${timestamp}_${fileObj.name.replace(
            /\.[^/.]+$/,
            ''
          )}.jpg`
        )
        await uploadBytes(thumbRef, thumbnailBlob)
        thumbnailURL = await getDownloadURL(thumbRef)
      }
    } catch (thumbError) {
      console.error('Error generating thumbnail:', thumbError)
    }

    return {
      id: fileId,
      photo_url: downloadURL,
      created_at: new Date().toISOString(),
      pin_id: pinId,
      user_id: userId,
      fileType: file.type,
      fileSize: file.size,
      thumbnail: thumbnailURL
    }
  })

  return Promise.all(uploads)
}

